import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ProjectsApi } from "@/services/api/projects";
import { CreateProjectModal } from "@/components/project/CreateProjectModal";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Project } from "@/types";
import type { AgentNotification } from "@/services/api/agentApi";
import { logger } from "@/utils";
import {
  Plus,
  Folder,
  FolderOpen,
  Clock,
  Bell,
  BellOff,
  AlertTriangle,
  Info,
  CircleAlert,
  CircleCheck,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AIUsageCard } from "@/components/dashboard/AIUsageCard";
import { OrganizationsCard } from "@/components/dashboard/OrganizationsCard";
import { ConnectionsCard } from "@/components/dashboard/ConnectionsCard";
import { MessagesCard } from "@/components/dashboard/MessagesCard";
import { PendingInvitationsBanner } from "@/components/dashboard/PendingInvitationsBanner";
import { TransferManagementPanel } from "@/components/project/TransferManagementPanel";
import { ProjectCardSkeletonList } from "@/components/dashboard/ProjectCardSkeleton";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useNotificationStore } from "@/stores/notifications/notification-store";
import { OwnerContext } from "@/services/api/projects";
import { getSafeNotificationNavigationTarget } from "@/components/notifications/notificationNavigation";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasProjectArray = (value: unknown): value is { projects: Project[] } =>
  isRecord(value) && Array.isArray(value.projects);

const isUnreadNotification = (notification: AgentNotification): boolean =>
  notification.status === "pending" || notification.status === "delivered";

const getUrgencyIcon = (urgency: AgentNotification["urgency"]) => {
  switch (urgency) {
    case "critical":
      return <CircleAlert className="h-4 w-4 text-red-500" />;
    case "high":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "low":
      return <Info className="h-4 w-4 text-muted-foreground" />;
    case "normal":
    default:
      return <CircleCheck className="h-4 w-4 text-primary" />;
  }
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { extendSession } = useAuthSession();
  const navigate = useNavigate();
  const { organizations, currentContext } = useOrganizationStore();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markRead,
  } = useNotificationStore();

  // Check if user is owner/admin of any organization
  const isOrgAdmin = organizations.some(
    (org) => org.membership?.role === "owner" || org.membership?.role === "admin"
  );

  // State
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isContextSwitching, setIsContextSwitching] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRecentProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build owner context inside function to avoid stale closure
      const { currentContext: ctx } = useOrganizationStore.getState();
      const ownerContext: OwnerContext | undefined =
        ctx?.type === "organization" && ctx.orgSlug
          ? { type: "organization", orgSlug: ctx.orgSlug }
          : undefined;

      const response = await ProjectsApi.getRecentProjects(5, ownerContext);
      logger.info("Recent projects API response:", {
        response,
        ownerContext,
        contextType: ctx?.type,
      });

      if (response.success && response.data) {
        const responseData: unknown = response.data;
        // Handle both array and object response formats for backward compatibility
        const projects = Array.isArray(responseData)
          ? responseData
          : hasProjectArray(responseData)
            ? responseData.projects
            : [];
        setRecentProjects(projects);
        logger.info("Recent projects loaded successfully:", {
          count: projects.length,
          firstProject: projects[0],
          contextType: ownerContext?.type,
        });
      } else {
        logger.warn("Recent projects API returned error:", response);
        setError("Failed to load recent projects");
      }
    } catch (err) {
      logger.error("Error loading recent projects:", err);
      setError("Failed to load recent projects");

      // Fallback: try to load from regular projects API as recent
      try {
        logger.info("Attempting fallback to regular projects API");
        // Get fresh context for fallback
        const { currentContext: fallbackCtx } = useOrganizationStore.getState();
        const fallbackOwnerContext: OwnerContext | undefined =
          fallbackCtx?.type === "organization" && fallbackCtx.orgSlug
            ? { type: "organization", orgSlug: fallbackCtx.orgSlug }
            : undefined;

        const fallbackResponse = await ProjectsApi.getProjects(
          {
            limit: 5,
            sortBy: "updatedAt",
            sortOrder: "desc",
          },
          fallbackOwnerContext
        );

        if (fallbackResponse.success && fallbackResponse.data?.projects) {
          setRecentProjects(fallbackResponse.data.projects);
          setError(null); // Clear error since fallback worked
          logger.info("Fallback projects loaded successfully:", {
            count: fallbackResponse.data.projects.length,
            contextType: fallbackOwnerContext?.type,
          });
        }
      } catch (fallbackErr) {
        logger.error("Fallback projects loading also failed:", fallbackErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load recent projects on mount
  useEffect(() => {
    void loadRecentProjects();
    void fetchNotifications(5);
  }, [fetchNotifications, loadRecentProjects]);

  // Reload projects when organization context changes
  useEffect(() => {
    if (currentContext) {
      setIsContextSwitching(true);
      void loadRecentProjects().finally(() => setIsContextSwitching(false));
    }
  }, [currentContext?.type, currentContext?.id, loadRecentProjects]);

  const handleCreateProject = async (data: { name: string; description: string; isPrivate: boolean }) => {
    if (!user?.id) {
      throw new Error("You must be logged in to create a project");
    }

    // Extend session before creating project
    extendSession();

    // Get fresh context to determine ownership
    const { currentContext: ctx } = useOrganizationStore.getState();

    // Determine owner based on current context
    const isOrgContext = ctx?.type === "organization" && ctx.id;
    const owner_type = isOrgContext ? ("Organization" as const) : ("User" as const);
    const owner_id = isOrgContext ? ctx.id : user.id;

    const createRequest = {
      ...data,
      isMultiPage: true,
      owner_type,
      owner_id,
      initialPages: [
        {
          name: "Home",
          description: "Main overview and dashboard",
          template: "BLANK",
          isDefault: true,
        },
      ],
    };

    // Build ownerContext for API routing
    const ownerContext: OwnerContext | undefined =
      isOrgContext && ctx?.orgSlug
        ? { type: "organization", orgSlug: ctx.orgSlug }
        : undefined;

    const response = await ProjectsApi.createProject(createRequest, ownerContext);

    if (response.success && response.data) {
      logger.info("Project created successfully", { projectId: response.data.id });
      setShowCreateDialog(false);
      await loadRecentProjects();
      navigate(`/viewer/${response.data.id}`);
    } else {
      throw new Error("Failed to create project");
    }
  };

  const handleOpenProject = (project: Project) => {
    const projectId = project.id;
    console.log("🎯 [DASHBOARD] Project clicked:", {
      project,
      projectId,
      projectIdType: typeof projectId,
      targetUrl: `/viewer/${projectId}`,
      timestamp: new Date().toISOString()
    });
    logger.info("Opening project from dashboard:", {
      project,
      projectId,
      projectIdType: typeof projectId,
    });
    extendSession();
    navigate(`/viewer/${projectId}`);
  };

  const handleViewAllProjects = () => {
    navigate("/projects");
  };

  const handleNotificationClick = async (notification: AgentNotification) => {
    if (isUnreadNotification(notification)) {
      await markRead(notification.notification_id);
    }

    const navigationTarget = getSafeNotificationNavigationTarget(
      notification.action_url,
    );
    if (navigationTarget) {
      navigate(navigationTarget);
    }
  };

  const recentNotifications = notifications.slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">
          Welcome back, {user?.firstName || "Developer"}!
        </h1>
        <p className="text-muted-foreground">
          Ready to build something amazing? Here's what's happening with your
          projects.
        </p>
      </div>

      {/* Pending Invitations Banner */}
      <PendingInvitationsBanner />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        {/* Row 1: Recent Projects (2) | Notifications (2) | Messages (2) */}

        {/* Recent Projects Card */}
        <Card className="lg:col-span-2 h-full flex flex-col">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Projects
                </CardTitle>
                <CardDescription>Your recently accessed projects</CardDescription>
              </div>
              <div className="flex items-center gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => setShowCreateDialog(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create New Project</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={handleViewAllProjects}
                      >
                        <FolderOpen className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Browse All Projects</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            {isLoading || isContextSwitching ? (
              <ProjectCardSkeletonList count={3} />
            ) : recentProjects.length > 0 ? (
              <div className="space-y-2 max-h-[180px] overflow-y-auto">
                {recentProjects.slice(0, 3).map((project) => (
                  <div
                    key={project.id || `project-${Math.random()}`}
                    className="flex items-center justify-between p-2 rounded border hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleOpenProject(project)}
                  >
                    <div className="flex items-center gap-2">
                      <Folder className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {project.name}
                      </span>
                    </div>
                  </div>
                ))}
                {recentProjects.length > 3 && (
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={handleViewAllProjects}
                  >
                    View all projects
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  {error
                    ? "Unable to load recent projects"
                    : "No recent projects"}
                </p>
                <div className="flex flex-col gap-2 mt-2">
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    Create your first project
                  </Button>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-xs"
                    onClick={handleViewAllProjects}
                  >
                    Browse all projects
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications Card - Equal width */}
        <Card className="lg:col-span-2 h-full flex flex-col">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notifications
                </CardTitle>
                <CardDescription>Recent activity</CardDescription>
              </div>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="h-5 min-w-[20px] justify-center px-1.5 text-xs">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            {recentNotifications.length > 0 ? (
              <div className="space-y-2 max-h-[220px] overflow-y-auto">
                {recentNotifications.map((notification) => {
                  const unread = isUnreadNotification(notification);
                  return (
                    <button
                      key={notification.notification_id}
                      type="button"
                      className="w-full rounded border p-2 text-left transition-colors hover:bg-muted/50"
                      onClick={() => {
                        void handleNotificationClick(notification);
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 flex-none">
                          {getUrgencyIcon(notification.urgency)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="truncate text-sm font-medium">
                              {notification.title}
                            </p>
                            {unread && (
                              <span className="mt-1 h-2 w-2 flex-none rounded-full bg-primary" />
                            )}
                          </div>
                          <p className="line-clamp-2 text-xs text-muted-foreground">
                            {notification.body}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <BellOff className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No new alerts</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages Card - Moved to top-right */}
        <div className="lg:col-span-2 h-full">
          <MessagesCard />
        </div>

        {/* Row 2: Organizations (4) | AI Usage (2) */}

        {/* Organizations Card - Wider */}
        <div className="lg:col-span-4 h-full">
          <OrganizationsCard />
        </div>

        {/* AI Usage Card - More compact */}
        <div className="lg:col-span-2 h-full">
          <AIUsageCard />
        </div>

        {/* Row 3: Connections (2) | Transfer Requests (4) */}

        {/* Connections Card - Condensed */}
        <div className="lg:col-span-2 h-full">
          <ConnectionsCard />
        </div>

        {/* Project Transfer Management Panel - Inline in grid (for organization admins) */}
        {isOrgAdmin && (
          <div className="lg:col-span-4 h-full">
            <TransferManagementPanel />
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/15 text-destructive px-4 py-2 rounded">
          {error}
        </div>
      )}

      <CreateProjectModal
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateProject}
      />
    </div>
  );
};
